import { Milliseconds } from '@/utils/milliseconds'

export type OrdemStatus =
  | 'ORCAMENTO'
  | 'APROVACAO'
  | 'EXECUCAO'
  | 'FINALIZADO'
  | 'CANCELADO'

interface OrdemProps {
  id: number
  abertaEm: Date
  aprovadoEm: Date | null
  entregaEm: Date | null
  clienteNome: string
  equipamentoNome: string
  defeitoReclamado: string | null
  defeitoConstatado: string | null
  condicaoPgto: string | null
  tipoBloqueio: string | null
  observacao: string | null
  status: OrdemStatus
}

export class Ordem {
  constructor(private readonly props: OrdemProps) {}

  get id(): number {
    return this.props.id
  }

  get abertaEm() {
    return this.props.abertaEm
  }

  get aprovadoEm() {
    return this.props.aprovadoEm
  }

  get entregaEm() {
    return this.props.entregaEm
  }

  get clienteNome() {
    return this.props.clienteNome
  }

  get equipamentoNome() {
    return this.props.equipamentoNome
  }

  get defeitoReclamado() {
    return this.props.defeitoReclamado
  }

  get defeitoConstatado() {
    return this.props.defeitoConstatado
  }

  get condicaoPgto() {
    return this.props.condicaoPgto
  }

  get tipoBloqueio() {
    return this.props.tipoBloqueio
  }

  get observacao() {
    return this.props.observacao
  }

  get status() {
    return this.props.status
  }

  get dataMaxOrcamento(): Date {
    return new Date(
      new Date(this.abertaEm).getTime() + Milliseconds({ days: 7 }),
    )
  }

  get entregaDiasRestantes(): number | null {
    if (!this.entregaEm) return null

    const diff = this.entregaEm.getTime() - new Date().getTime()
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  }

  get entregaProgress(): number | null {
    if (!this.aprovadoEm || !this.entregaEm) return null

    const total = this.entregaEm.getTime() - this.aprovadoEm.getTime()
    const progress = new Date().getTime() - this.aprovadoEm.getTime()
    return Math.min(Math.max(Math.ceil((progress / total) * 100), 0), 100)
  }

  get progresso(): { valor: number; diasRestantes: number } {
    if (!this.aprovadoEm) {
      // Ordem ainda não foi aprovada
      const abertaEm = new Date(this.abertaEm).getTime()
      const now = Date.now()

      const elapsed = now - abertaEm
      const total = this.dataMaxOrcamento.getTime() - abertaEm
      return {
        valor: Math.min((elapsed / total) * 100, 100),
        diasRestantes: 7 - Math.floor(elapsed / (1000 * 60 * 60 * 24)),
      }
    }

    const aprovadoEm = new Date(this.aprovadoEm).getTime()
    if (!this.entregaEm) {
      // Ordem aprovada, mas sem data de entrega definida
      return {
        valor: 0,
        diasRestantes: 0,
      }
    }

    // Ordem aprovada e com data de entrega definida
    const entregaEm = new Date(this.entregaEm).getTime()

    const elapsed = entregaEm - aprovadoEm
    const total = entregaEm - aprovadoEm

    return {
      valor: Math.min((elapsed / total) * 100, 100),
      diasRestantes: Math.max(
        0,
        7 - Math.floor(elapsed / (1000 * 60 * 60 * 24)),
      ),
    }
  }
}
